import React, { useContext, Fragment, useEffect } from "react";
import Checkbox from "../../components/data_entry/checkbox";
import Input from "../../components/data_entry/input";
import {
  ModeOfCommunication,
  ConversationTopic,
  ProgramSegmentDocument,
} from "../../generated/graphql";
import { gql } from "@urql/core";
import { SurveyParticipantDataDocument, ConversationTopicsDocument } from "../../generated/graphql";
import { SurveyContext, QuestionProps } from "../survey";
import QuestionLabel from "./components/question_label";
import styled from "styled-components";
import { useQuery } from "urql";

export interface Answer {
  modesOfCommunication: { standard: ModeOfCommunication[]; writeIn?: string };
  conversationTopics: { standard: ConversationTopic[]; writeIn?: string };
}

export interface Customizations {
  modeOfCommunicationPrompt: string;
  topicsPrompt: string;
  description?: string;
}

const modeOfCommunicationOptions = [
  {
    label: "Videochat (Skype, Facetime, etc)",
    value: ModeOfCommunication.Videochat,
  },
  {
    label: "Email",
    value: ModeOfCommunication.Email,
  },
  {
    label: "Talking on the phone",
    value: ModeOfCommunication.TalkingOnPhone,
  },
  {
    label: "Texting",
    value: ModeOfCommunication.Texting,
  },
  {
    label: "Social media (Instagram, Facebook, Snapchat, LinkedIn)",
    value: ModeOfCommunication.SocialMedia,
  },
  {
    label: "Meeting in person",
    value: ModeOfCommunication.MeetingInPerson,
  },
  { label: "Other", value: ModeOfCommunication.Other },
];

function conversationTopicLabel(topic: ConversationTopic): string {
  switch (topic) {
    case ConversationTopic.GettingToKnowEachOther:
      return "Getting to know each other";
    case ConversationTopic.Career:
      return "Career (jobs, internships, what to do after graduation)";
    case ConversationTopic.CorporateCareer:
      return "Career";
    case ConversationTopic.Activities:
      return "Activities (sports, clubs, being a leader on campus)";
    case ConversationTopic.SchoolLife:
      return "School life (eating, weather, culture)";
    case ConversationTopic.StayingBalanced:
      return "Staying balanced (wellness, anxiety, stress, self-care)";
    case ConversationTopic.Academics:
      return "Academics (classes, instructors, grades, studying)";
    case ConversationTopic.FamilyLife:
      return "Family life";
    case ConversationTopic.GraduateSchool:
      return "Graduate school";
    case ConversationTopic.Resumes:
      return "Resumes";
    case ConversationTopic.Interviewing:
      return "Interviewing";
    case ConversationTopic.LinkedIn:
      return "LinkedIn";
    case ConversationTopic.CareerFairs:
      return "Career fairs";
    case ConversationTopic.Networking:
      return "Networking";
    case ConversationTopic.ConnectingCourseworkToCareer:
      return "Connecting coursework to career";
    case ConversationTopic.ResearchOpportunities:
      return "Research opportunities";
    case ConversationTopic.StudentOrCampusLife:
      return "Student or campus life";
    case ConversationTopic.ComparingSchools:
      return "Comparing schools";
    case ConversationTopic.FinancialAid:
      return "Financial aid";
    case ConversationTopic.OnboardingToCompanyOrDepartment:
      return "Onboarding to Company/Department";
    case ConversationTopic.ProfessionalDevelopment:
      return "Professional Development";
    case ConversationTopic.WorkLifeBalance:
      return "Work/Life Balance";
    case ConversationTopic.GettingInvolvedAndCompanyProgramming:
      return "Getting Involved/Company Programming";
    case ConversationTopic.CurrentEvents:
      return "Current Events";
    case ConversationTopic.OvercomingObstaclesAndNavigatingTheWorkplace:
      return "Overcoming Obstacles/Navigating the Workplace";
    case ConversationTopic.ManagingUp:
      return "Managing Up";
    case ConversationTopic.BuildingProfessionalBrand:
      return "Building Professional Brand";
    case ConversationTopic.BenefitsAndCompensation:
      return "Benefits & Compensation";
    case ConversationTopic.PersonnelChallenges:
      return "Personnel Challenges";
    case ConversationTopic.Other:
      return "Other";
    default:
      throw new Error(`Unrecognized conversation topic ${topic}`);
  }
}

gql`
  query SurveyParticipantData($participantId: ID!) {
    participant(id: $participantId) {
      id
      program {
        id
        segment
        programSequence {
          id
          school {
            id
            shortName
          }
        }
      }
    }
  }
`;

gql`
  query ConversationTopics($segment: ProgramSegment!, $programId: ID!, $questionId: ID!) {
    conversationTopics(segment: $segment, programId: $programId, questionId: $questionId)
  }
`;

gql`
  query ProgramSegment($programId: ID!) {
    program(id: $programId) {
      id
      segment
      programSequence {
        id
        school {
          id
          shortName
        }
      }
    }
  }
`;

const ConversationTopicsQuestion = styled.div`
  margin-top: ${({ theme }) => theme.deprecatedSpacing.component.eight};
`;

export default function Conversation({
  customizations,
  currentAnswer,
  updateAnswer,
  setAbleToSubmit,
  questionId,
}: QuestionProps<Answer, Customizations>) {
  const { participantId, programId = "" } = useContext(SurveyContext);

  const [{ fetching, error, data }] = useQuery({
    query: SurveyParticipantDataDocument,
    variables: { participantId },
    pause: !participantId,
  });

  const [
    { fetching: fetchingProgramSegment, error: errorProgramSegment, data: programSegmentData },
  ] = useQuery({
    query: ProgramSegmentDocument,
    variables: { programId: programId },
    pause: !programId,
  });

  const [{ fetching: fetchingTopics, data: topicsData }] = useQuery({
    query: ConversationTopicsDocument,
    variables: {
      // The conversation topics are based on the program segment, and the program segment could be obtained
      // from either the current participant or the program (from the secret survey preview)
      // When coming from the secret survey preview, the programId will be set and segment retrieved from there
      // instead of from the participant

      // checking this in the pause
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      segment: data?.participant.program.segment || programSegmentData?.program.segment,
      programId: programId,
      questionId: questionId ? questionId.toString() : "",
    },
    pause: !data?.participant.program.segment && !programSegmentData?.program.segment,
  });

  let modeOfCommunicationLabel = "";
  let topicsLabel = "";

  const answeredConversationTopicsQuestion =
    !!currentAnswer &&
    (currentAnswer.conversationTopics.standard.length > 0 ||
      (currentAnswer.conversationTopics.writeIn?.length || 0) > 0);

  const answeredModesOfCommunicationQuestion =
    !!currentAnswer &&
    (currentAnswer.modesOfCommunication.standard.length > 0 ||
      (currentAnswer.modesOfCommunication.writeIn?.length || 0) > 0);

  const answerIsValid =
    typeof currentAnswer !== "undefined" &&
    (!topicsLabel || answeredConversationTopicsQuestion) &&
    (!modeOfCommunicationLabel || answeredModesOfCommunicationQuestion);

  useEffect(() => {
    setAbleToSubmit(answerIsValid);
  }, [answerIsValid, setAbleToSubmit]);

  if (fetching || data === null || fetchingProgramSegment || programSegmentData === null)
    return <div>Loading...</div>;

  if ((error || !data) && (errorProgramSegment || !programSegmentData)) return <div>Uh oh</div>;

  modeOfCommunicationLabel = customizations.modeOfCommunicationPrompt;
  const description = customizations.description;
  topicsLabel = customizations.topicsPrompt;
  const answer = currentAnswer
    ? currentAnswer
    : { modesOfCommunication: { standard: [] }, conversationTopics: { standard: [] } };

  return (
    <div>
      {modeOfCommunicationLabel && (
        <div>
          <QuestionLabel label={modeOfCommunicationLabel} description={description} />
          {modeOfCommunicationOptions.map((option) => (
            <Fragment key={option.value}>
              <Checkbox
                dataTest="conversation-log-mode-of-communication"
                label={option.label}
                checked={answer.modesOfCommunication.standard.includes(option.value)}
                onChange={() => {
                  const index = answer.modesOfCommunication.standard.indexOf(option.value);
                  if (index > -1) {
                    updateAnswer({
                      conversationTopics: answer.conversationTopics,
                      modesOfCommunication: {
                        standard: [
                          ...answer.modesOfCommunication.standard.slice(0, index),
                          ...answer.modesOfCommunication.standard.slice(index + 1),
                        ],
                        writeIn:
                          option.value === ModeOfCommunication.Other
                            ? undefined
                            : answer.modesOfCommunication.writeIn,
                      },
                    });
                  } else {
                    updateAnswer({
                      conversationTopics: answer.conversationTopics,
                      modesOfCommunication: {
                        ...answer.modesOfCommunication,
                        standard: [...answer.modesOfCommunication.standard, option.value],
                      },
                    });
                  }
                }}
              />
              {option.value === ModeOfCommunication.Other &&
                answer.modesOfCommunication.standard.includes(option.value) && (
                  <Input
                    type="text"
                    value={
                      answer.modesOfCommunication.writeIn ? answer.modesOfCommunication.writeIn : ""
                    }
                    onChange={({ currentTarget: { value: updatedWriteIn } }) =>
                      updateAnswer({
                        conversationTopics: answer.conversationTopics,
                        modesOfCommunication: {
                          ...answer.modesOfCommunication,
                          writeIn: updatedWriteIn,
                        },
                      })
                    }
                  />
                )}
            </Fragment>
          ))}
        </div>
      )}

      {fetchingTopics || topicsData === null ? (
        <div>Loading...</div>
      ) : !topicsData ? (
        <div>Uh oh</div>
      ) : (
        <ConversationTopicsQuestion>
          <QuestionLabel label={topicsLabel} description={description} />
          {topicsData.conversationTopics.map((topic) => (
            <Fragment key={topic}>
              <Checkbox
                dataTest="conversation-log-topic"
                label={conversationTopicLabel(topic)}
                checked={answer.conversationTopics.standard.includes(topic)}
                onChange={() => {
                  const index = answer.conversationTopics.standard.indexOf(topic);
                  if (index > -1) {
                    updateAnswer({
                      modesOfCommunication: answer.modesOfCommunication,
                      conversationTopics: {
                        standard: [
                          ...answer.conversationTopics.standard.slice(0, index),
                          ...answer.conversationTopics.standard.slice(index + 1),
                        ],
                        writeIn:
                          topic === ConversationTopic.Other
                            ? undefined
                            : answer.conversationTopics.writeIn,
                      },
                    });
                  } else {
                    updateAnswer({
                      modesOfCommunication: answer.modesOfCommunication,
                      conversationTopics: {
                        ...answer.conversationTopics,
                        standard: [...answer.conversationTopics.standard, topic],
                      },
                    });
                  }
                }}
              />
              {topic === ConversationTopic.Other &&
                answer.conversationTopics.standard.includes(topic) && (
                  <Input
                    type="text"
                    value={
                      answer.conversationTopics.writeIn ? answer.conversationTopics.writeIn : ""
                    }
                    onChange={({ currentTarget: { value: updatedWriteIn } }) =>
                      updateAnswer({
                        modesOfCommunication: answer.modesOfCommunication,
                        conversationTopics: {
                          ...answer.conversationTopics,
                          writeIn: updatedWriteIn,
                        },
                      })
                    }
                  />
                )}
            </Fragment>
          ))}
        </ConversationTopicsQuestion>
      )}
    </div>
  );
}
